/** A visible stretch of an x axis, in its own units — timestamps on a date axis. */
export interface ChartRange {
  min: number
  max: number
}

/**
 * Fits `range` inside `extent` without changing its width where it can: a window pushed
 * past an edge slides back rather than shrinking, and one wider than the data becomes the
 * data.
 */
export function clampRange(range: ChartRange, extent: ChartRange): ChartRange {
  const width = range.max - range.min

  if (width >= extent.max - extent.min) return { ...extent }
  if (range.min < extent.min)
    return { min: extent.min, max: extent.min + width }
  if (range.max > extent.max)
    return { min: extent.max - width, max: extent.max }

  return { ...range }
}

/**
 * Scales `range` by `factor` around `anchor` — the point under the cursor stays where it
 * is, which is what makes a wheel zoom feel like it zooms *into* something. `factor` above
 * 1 zooms out. Never narrower than `minWidth`, never outside `extent`.
 */
export function zoomRange(
  range: ChartRange,
  extent: ChartRange,
  anchor: number,
  factor: number,
  minWidth: number
): ChartRange {
  const width = Math.max((range.max - range.min) * factor, minWidth)
  const share = (anchor - range.min) / (range.max - range.min)
  const min = anchor - width * share

  return clampRange({ min, max: min + width }, extent)
}

/** Moves `range` by `delta` without leaving `extent`; its width is kept. */
export function panRange(
  range: ChartRange,
  extent: ChartRange,
  delta: number
): ChartRange {
  return clampRange({ min: range.min + delta, max: range.max + delta }, extent)
}
